import {
  List,
  ListItem,
  compareAccountsByName,
  getAccountName,
  mapAccountNamesById
} from "@dealer-center/core-components";
import { LoadingIndicator } from "@dealer-center/core-custom-components";
import { AccountListItem } from "./AccountListItem";
import styled from "styled-components";
import { useGetAccountNames } from "@dealer-center/ddc-api-account-fetch";
import type { AccountNames } from "@dealer-center/ddc-api-account-interface";

const StyledListContainerWrapper = styled.div`
  .list-container__header-title {
    height: 36px;
    padding-bottom: 4px;
    margin: 0;
    font-size: 16px;
  }
`;

export function AccountList({
  accounts,
  onDelete,
  shouldSortByAccountName = false,
  title
}: {
  accounts?: readonly string[];
  onDelete?: (account: string) => void;
  shouldSortByAccountName?: boolean;
  title: string;
}) {
  const {
    data: accountNames,
    isLoading,
    isPlaceholderData
  } = useGetAccountNames(accounts);

  const htmlIdSafeTitle = title.replace(/\s/g, "-");
  // Render in the order the caller supplied — most-recently-used first for the
  // History tab (US2054268) — rather than the order the account-name service
  // returns, which follows the sorted ids `useGetAccountNames` requests.
  const accountNamesById = mapAccountNamesById(accountNames);
  // Favorites are listed alphabetically by account name, with the same
  // comparator as the navbar account menu so both show the same order.
  const orderedAccountIds = shouldSortByAccountName
    ? [...(accounts ?? [])].sort(compareAccountsByName(accountNamesById))
    : accounts;
  const accountsToUse: AccountNames[] | undefined = orderedAccountIds?.map(
    accountId => ({
      accountId,
      accountName: getAccountName(accountNamesById, accountId)
    })
  );
  // Sorting by name needs the names, so hold the list back until the first
  // names arrive. Rendering early would show rows that visibly reshuffle once
  // the names land. After that, a changed list (e.g. un-favoriting an account)
  // keeps the previous names as placeholder data, so the list stays on screen
  // instead of blinking back to a spinner on every change. Lists that keep the
  // supplied order (the History tab) render straight away.
  // The `!!accounts?.length` guard is defensive: with nothing to look up the
  // query is disabled, and React Query v5 already reports a disabled query as
  // not loading (`isLoading` is `isPending && isFetching`). The guard keeps an
  // empty list from ever waiting on names regardless of how `isLoading` is
  // derived.
  const isAwaitingAccountNames =
    shouldSortByAccountName && isLoading && !accountNames && !!accounts?.length;
  // Placeholder names come from the previous id list, so an account that is new
  // to this list is still waiting on its name.
  const isFetchingAccountNames = isLoading || isPlaceholderData;
  return (
    <StyledListContainerWrapper>
      <h2>{title}</h2>
      {isAwaitingAccountNames && (
        <LoadingIndicator actionName={`Loading ${title}`} />
      )}
      {!isAwaitingAccountNames && accountsToUse && accountsToUse.length > 0 && (
        <List>
          {accountsToUse.map(account => {
            return (
              <ListItem
                key={`${htmlIdSafeTitle}-AccountList-item-${account.accountId}`}
                sx={{
                  border: 0,
                  margin: 0,
                  padding: 0
                }}
              >
                <AccountListItem
                  account={account}
                  isLoadingAccountName={
                    isFetchingAccountNames && !account.accountName
                  }
                  testId={`${htmlIdSafeTitle}-AccountList-item-${account.accountId}`}
                  onDelete={onDelete}
                />
              </ListItem>
            );
          })}
        </List>
      )}
    </StyledListContainerWrapper>
  );
}
